import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "../../services/api";
import Toast from "../../components/Toast";
import { formatMessageTime } from "../../utils/messageTime";
import TablePagination from "../company/JobManagement/Components/TablePagination";
interface Conversation {
  id: string;
  subject: string;
  job_seeker_email: string;
  job_seeker_has_account: boolean;
  status: string;
  priority: string;
  last_message_at: string;
  last_message_by: string;
  message_count: number;
  company_unread_count: number;
  latest_message?: {
    content: string;
    sender_name: string;
    created_at: string;
  };
  company_name?: string;
  job_title?: string;
  is_private: boolean;
  sharing_type: string;
  owner_user_id: string;
  permission_level: string;
  is_owner: boolean;
}

interface ConversationsResponse {
  conversations: Conversation[];
  total: number;
  has_more: boolean;
  view_type: string;
}

interface DropdownMenuProps {
  conversation: Conversation;
  onStatusChange: (conversationId: string, newStatus: string) => void;
  onPriorityChange: (conversationId: string, newPriority: string) => void;
  onShareConversation: (conversationId: string) => void;
  onTransferConversation: (conversationId: string) => void;
  onClose: () => void;
  onViewConversation: (conversationId: string) => void;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  conversation,
  onStatusChange,
  onPriorityChange,
  onShareConversation,
  onTransferConversation,
  onClose,
  onViewConversation,
}) => {
  const [showStatusSubmenu, setShowStatusSubmenu] = useState(false);
  const [showPrioritySubmenu, setShowPrioritySubmenu] = useState(false);

  const statusOptions = [
    { value: "active", label: "Active", icon: "🟢" },
    { value: "archived", label: "Archive", icon: "📁" },
    { value: "closed", label: "Close", icon: "🔒" },
  ];

  const priorityOptions = [
    { value: "low", label: "Low", icon: "🟢", color: "text-green-600" },
    { value: "normal", label: "Normal", icon: "🟡", color: "text-yellow-600" },
    { value: "high", label: "High", icon: "🟠", color: "text-orange-600" },
    { value: "urgent", label: "Urgent", icon: "🔴", color: "text-red-600" },
  ];

  return (
    <div
      className="absolute right-0 top-8 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="py-1">
        {/* Status Section */}
        <div
          className="relative"
          onMouseEnter={() => setShowStatusSubmenu(true)}
          onMouseLeave={() => setShowStatusSubmenu(false)}
        >
          <button
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="flex items-center">
              <span className="mr-2">📋</span>
              Change Status
            </span>
            <span>‹</span>
          </button>

          {showStatusSubmenu && (
            <div
              className="absolute right-full top-0 w-40 bg-white border border-gray-200 rounded-lg shadow-lg mr-1"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="py-1">
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      onStatusChange(conversation.id, option.value);
                      onClose();
                    }}
                    disabled={conversation.status === option.value}
                    className={`w-full text-left px-4 py-2 text-sm flex items-center hover:bg-gray-100 ${conversation.status === option.value
                      ? "text-gray-400 cursor-not-allowed bg-gray-50"
                      : "text-gray-700"
                      }`}
                  >
                    <span className="mr-2">{option.icon}</span>
                    {option.label}
                    {conversation.status === option.value && (
                      <span className="ml-auto text-xs">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Priority Section */}
        <div
          className="relative"
          onMouseEnter={() => setShowPrioritySubmenu(true)}
          onMouseLeave={() => setShowPrioritySubmenu(false)}
        >
          <button
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="flex items-center">
              <span className="mr-2">⚡</span>
              Change Priority
            </span>
            <span>‹</span>
          </button>

          {showPrioritySubmenu && (
            <div
              className="absolute right-full top-0 w-40 bg-white border border-gray-200 rounded-lg shadow-lg mr-1"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="py-1">
                {priorityOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      onPriorityChange(conversation.id, option.value);
                      onClose();
                    }}
                    disabled={conversation.priority === option.value}
                    className={`w-full text-left px-4 py-2 text-sm flex items-center hover:bg-gray-100 ${conversation.priority === option.value
                      ? "text-gray-400 cursor-not-allowed bg-gray-50"
                      : option.color
                      }`}
                  >
                    <span className="mr-2">{option.icon}</span>
                    {option.label}
                    {conversation.priority === option.value && (
                      <span className="ml-auto text-xs">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-gray-100 my-1"></div>

        {/* Privacy Controls - Only show for owners */}
        {conversation.is_owner && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
                onShareConversation(conversation.id);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            >
              <span className="mr-2">👥</span>
              Share Conversation
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
                onTransferConversation(conversation.id);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            >
              <span className="mr-2">🔄</span>
              Transfer Ownership
            </button>

            <div className="border-t border-gray-100 my-1"></div>
          </>
        )}

        {/* Quick Actions */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
            onViewConversation(conversation.id);
          }}
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
        >
          <span className="mr-2">👁️</span>
          View Conversation
        </button>
      </div>
    </div>
  );
};

// Share Conversation Modal Component
interface ShareConversationModalProps {
  conversationId: string;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const ShareConversationModal: React.FC<ShareConversationModalProps> = ({
  conversationId,
  onClose,
  onSuccess,
  onError,
}) => {
  const [sharingType, setSharingType] = useState<
    "company_wide" | "specific_users" | "private"
  >("specific_users");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [companyUsers, setCompanyUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingShares, setLoadingShares] = useState(true);

  useEffect(() => {
    // Load company users and current shares
    const loadData = async () => {
      try {
        setLoadingUsers(true);
        setLoadingShares(true);

        // Load company users
        const users = await apiService.getCompanyUsers("current", {
          status_filter: "active",
        });
        setCompanyUsers(users || []);

        // Load current conversation shares by checking the conversation details
        // We'll get this from the conversation list where the conversation should show sharing info
        const conversations = await apiService.getConversations(
          "active",
          "my_conversations",
        );
        const currentConversation = conversations.conversations.find(
          (c: Conversation) => c.id === conversationId,
        );

        if (currentConversation) {
          // If conversation is shared, we need to get the share details
          // For now, we'll show the sharing type and let users modify it
          setSharingType(currentConversation.sharing_type || "private");
        }
      } catch (error) {
        console.error("Failed to load data:", error);
        onError("Failed to load conversation data");
      } finally {
        setLoadingUsers(false);
        setLoadingShares(false);
      }
    };

    loadData();
  }, [conversationId]);

  const handleShare = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `/api/v1/conversations/${conversationId}/share`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify({
            sharing_type: sharingType,
            target_user_ids:
              sharingType === "specific_users" ? selectedUsers : [],
            permission_level: "collaborator",
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update conversation sharing");
      }

      const data = await response.json();

      if (sharingType === "private") {
        onSuccess("Conversation made private successfully");
      } else {
        onSuccess("Conversation sharing updated successfully");
      }
      onClose();
    } catch (error) {
      console.error("Share conversation error:", error);
      onError("Failed to update conversation sharing");
    } finally {
      setLoading(false);
    }
  };

  const getActionButtonText = () => {
    if (loading) return "Updating...";

    switch (sharingType) {
      case "private":
        return "Make Private";
      case "company_wide":
        return "Share with Company";
      case "specific_users":
        return selectedUsers.length > 0
          ? "Update Sharing"
          : "Remove All Shares";
      default:
        return "Update";
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Manage Conversation Sharing
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sharing Type
            </label>
            <select
              value={sharingType}
              onChange={(e) =>
                setSharingType(
                  e.target.value as
                  | "company_wide"
                  | "specific_users"
                  | "private",
                )
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="private">🔒 Private (No sharing)</option>
              <option value="specific_users">
                👥 Share with specific users
              </option>
              <option value="company_wide">🏢 Share with entire company</option>
            </select>
          </div>

          {sharingType === "private" && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <p className="text-sm text-yellow-800">
                ⚠️ Making this conversation private will remove access for all
                shared users.
              </p>
            </div>
          )}

          {sharingType === "company_wide" && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-800">
                ℹ️ All company members will be able to view this conversation.
              </p>
            </div>
          )}

          {sharingType === "specific_users" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Users
              </label>
              {loadingUsers ? (
                <div className="border border-gray-300 rounded-md p-4 text-center text-gray-500">
                  Loading users...
                </div>
              ) : companyUsers.length === 0 ? (
                <div className="border border-gray-300 rounded-md p-4 text-center text-gray-500">
                  No users found
                </div>
              ) : (
                <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                  {companyUsers.map((user) => (
                    <label
                      key={user.id}
                      className="flex items-center space-x-2 py-1"
                    >
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUsers([...selectedUsers, user.id]);
                          } else {
                            setSelectedUsers(
                              selectedUsers.filter((id) => id !== user.id),
                            );
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-sm">
                        {user.first_name} {user.last_name} ({user.email})
                      </span>
                    </label>
                  ))}
                </div>
              )}

              {selectedUsers.length === 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  No users selected. This will make the conversation private.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleShare}
            disabled={loading}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${sharingType === "private"
              ? "bg-red-600 hover:bg-red-700"
              : "bg-indigo-600 hover:bg-indigo-700"
              } disabled:opacity-50`}
          >
            {getActionButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};

// Transfer Ownership Modal Component
interface TransferOwnershipModalProps {
  conversationId: string;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const TransferOwnershipModal: React.FC<TransferOwnershipModalProps> = ({
  conversationId,
  onClose,
  onSuccess,
  onError,
}) => {
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [companyUsers, setCompanyUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    // Load company users for selection
    const loadCompanyUsers = async () => {
      try {
        setLoadingUsers(true);
        console.log("Loading company users for transfer...");
        const users = await apiService.getCompanyUsers("current", {
          status_filter: "active",
        });
        console.log("Loaded company users for transfer:", users);

        // Get current user ID from localStorage to filter out
        const currentUserData = localStorage.getItem("user");
        let currentUserId: string | null = null;
        if (currentUserData) {
          try {
            const parsedUser = JSON.parse(currentUserData);
            currentUserId = parsedUser.id;
          } catch (e) {
            console.error("Error parsing current user data:", e);
          }
        }

        // Filter out current user since you can't transfer ownership to yourself
        const filteredUsers = currentUserId
          ? users.filter((user) => user.id !== currentUserId)
          : users;

        console.log("Filtered users (excluding current user):", filteredUsers);
        setCompanyUsers(filteredUsers || []);
      } catch (error) {
        console.error("Failed to load company users:", error);
        onError("Failed to load company users");
        setCompanyUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    loadCompanyUsers();
  }, []); // Remove onError from dependencies to prevent infinite loop

  const handleTransfer = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `/api/v1/conversations/${conversationId}/transfer`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify({
            new_owner_id: selectedUserId,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to transfer ownership");
      }

      const data = await response.json();
      onSuccess("Ownership transferred successfully");
      onClose();
    } catch (error) {
      console.error("Transfer ownership error:", error);
      onError("Failed to transfer ownership");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Transfer Ownership
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Owner
            </label>
            {loadingUsers ? (
              <div className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-500">
                Loading users...
              </div>
            ) : (
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">Select a user...</option>
                {companyUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.email})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <p className="text-sm text-yellow-800">
              ⚠️ Warning: Once transferred, you will no longer be the owner of
              this conversation and may lose access to it.
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleTransfer}
            disabled={loading || !selectedUserId}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {loading ? "Transferring..." : "Transfer Ownership"}
          </button>
        </div>
      </div>
    </div>
  );
};

const CompanyMessages: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState("active");
  const [viewType, setViewType] = useState<
    "my_conversations" | "shared_with_me" | "all_company"
  >("my_conversations");
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [toast, setToast] = useState({
    isVisible: false,
    message: "",
    type: "success" as "success" | "error",
  });

  // Search and Filter States
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState<
    "all" | "email" | "name" | "content"
  >("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [accountStatusFilter, setAccountStatusFilter] = useState<string>("all");
  const [dateFilter, setDateFilter] = useState<string>("all");
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    loadConversations();
  }, [
    currentPage,
    rowsPerPage,
    selectedStatus,
    viewType,
    searchQuery,
    searchType,
    priorityFilter,
    accountStatusFilter,
    dateFilter,
  ]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * rowsPerPage;
      const response = await apiService.getConversations(
        selectedStatus,
        viewType,
        rowsPerPage,
        offset,
        searchQuery,
        priorityFilter,
        accountStatusFilter,
        dateFilter,
      );
      setConversations(response.conversations);
      setTotalCount(response.total);


      // Calculate total unread count from active conversations
      if (selectedStatus === "active") {
        const unreadCount = response.conversations.reduce(
          (sum: number, conv: Conversation) => sum + conv.company_unread_count,
          0,
        );
        setTotalUnreadCount(unreadCount);
      }
    } catch (error) {
      console.error("Failed to load conversations:", error);
      showToast("Failed to load conversations", "error");
    } finally {
      setLoading(false);
    }
  };

  // Clear search and filters
  const clearSearchAndFilters = () => {
    setSearchQuery("");
    setSearchType("all");
    setPriorityFilter("all");
    setAccountStatusFilter("all");
    setDateFilter("all");
  };

  // Get active filter count
  const getActiveFilterCount = () => {
    let count = 0;
    if (searchQuery.trim()) count++;
    if (priorityFilter !== "all") count++;
    if (accountStatusFilter !== "all") count++;
    if (dateFilter !== "all") count++;
    return count;
  };

  const handleStatusChange = async (
    conversationId: string,
    newStatus: string,
  ) => {
    try {
      await apiService.updateConversationStatus(conversationId, newStatus);

      // Update local state
      setConversations((prev) =>
        prev.filter((conv) => conv.id !== conversationId),
      );

      showToast(
        `Conversation ${newStatus === "archived"
          ? "archived"
          : newStatus === "closed"
            ? "closed"
            : "reactivated"
        } successfully`,
        "success",
      );
    } catch (error) {
      console.error("Failed to update conversation status:", error);
      showToast("Failed to update conversation status", "error");
    }
  };

  const handlePriorityChange = async (
    conversationId: string,
    newPriority: string,
  ) => {
    try {
      await apiService.updateConversationPriority(conversationId, newPriority);

      // Update local state
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId
            ? { ...conv, priority: newPriority }
            : conv,
        ),
      );

      showToast(`Priority updated to ${newPriority}`, "success");
    } catch (error) {
      console.error("Failed to update conversation priority:", error);
      showToast("Failed to update conversation priority", "error");
    }
  };

  const showToast = (message: string, type: "success" | "error") => {
    setToast({ isVisible: true, message, type });
    setTimeout(() => setToast((prev) => ({ ...prev, isVisible: false })), 5000);
  };

  const handleConversationClick = (conversationId: string) => {
    navigate(`/company/messages/${conversationId}`);
  };

  const handleViewConversation = (conversationId: string) => {
    navigate(`/company/messages/${conversationId}`);
  };

  const handleShareConversation = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setShowShareModal(true);
  };

  const handleTransferConversation = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setShowTransferModal(true);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "bg-red-100 text-red-800 border-red-200";
      case "high":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "normal":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "low":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const closeToast = () => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setActiveDropdown(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Messages</h1>
            <p className="mt-2 text-gray-600">
              Manage conversations with candidates and job seekers
            </p>
          </div>
          <div className="flex items-center space-x-4">
            {totalUnreadCount > 0 && (
              <div className="bg-red-100 border border-red-200 rounded-lg px-4 py-2">
                <span className="text-red-800 font-medium">
                  {totalUnreadCount} unread message
                  {totalUnreadCount > 1 ? "s" : ""}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="mb-6 space-y-4">
        {/* Search Bar */}
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);

              }}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>


          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-4 py-2 border rounded-lg transition-colors ${showFilters || getActiveFilterCount() > 0
              ? "border-indigo-500 bg-indigo-50 text-indigo-700"
              : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              }`}
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            Filters
            {getActiveFilterCount() > 0 && (
              <span className="ml-2 bg-indigo-600 text-white text-xs font-medium px-2 py-1 rounded-full">
                {getActiveFilterCount()}
              </span>
            )}
          </button>

          {/* Clear Filters */}
          {getActiveFilterCount() > 0 && (
            <button
              onClick={clearSearchAndFilters}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Clear All
            </button>
          )}
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Priority Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  value={priorityFilter}
                  onChange={(e) => {
                    setPriorityFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="all">All Priorities</option>
                  <option value="urgent">🔴 Urgent</option>
                  <option value="high">🟠 High</option>
                  <option value="normal">🟡 Normal</option>
                  <option value="low">🟢 Low</option>
                </select>
              </div>

              {/* Account Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Status
                </label>
                <select
                  value={accountStatusFilter}
                  onChange={(e) => {
                    setAccountStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="all">All Status</option>
                  <option value="active_user">Active Users</option>
                  <option value="invited">Invited Only</option>
                </select>
              </div>

              {/* Date Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date Range
                </label>
                <select
                  value={dateFilter}
                  onChange={(e) => {
                    setDateFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="older">Older than 30 days</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Active Filters Display */}
        {getActiveFilterCount() > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-600">Active filters:</span>
            {searchQuery.trim() && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Search: "{searchQuery}" ({searchType})
                <button
                  onClick={() => setSearchQuery("")}
                  className="ml-2 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            )}
            {priorityFilter !== "all" && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                Priority: {priorityFilter}
                <button
                  onClick={() => setPriorityFilter("all")}
                  className="ml-2 text-orange-600 hover:text-orange-800"
                >
                  ×
                </button>
              </span>
            )}
            {accountStatusFilter !== "all" && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Status:{" "}
                {accountStatusFilter === "active_user"
                  ? "Active Users"
                  : "Invited"}
                <button
                  onClick={() => setAccountStatusFilter("all")}
                  className="ml-2 text-green-600 hover:text-green-800"
                >
                  ×
                </button>
              </span>
            )}
            {dateFilter !== "all" && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Date: {dateFilter}
                <button
                  onClick={() => setDateFilter("all")}
                  className="ml-2 text-purple-600 hover:text-purple-800"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Status Filter */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: "active", name: "Active" },
              { id: "archived", name: "Archived" },
              { id: "closed", name: "Closed" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setSelectedStatus(tab.id);
                  setCurrentPage(1);
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${selectedStatus === tab.id
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
              >
                {tab.name}
                {selectedStatus === tab.id && conversations.length > 0 && (
                  <span className="ml-2 bg-indigo-100 text-indigo-600 py-0.5 px-2.5 rounded-full text-xs font-medium">
                    {conversations.length}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* View Type Filter */}
      <div className="mb-6">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">View:</span>
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
            {[
              { id: "my_conversations", name: "My Conversations", icon: "👤" },
              { id: "shared_with_me", name: "Shared with Me", icon: "👥" },
              { id: "all_company", name: "All Company", icon: "🏢" },
            ].map((view) => (
              <button
                key={view.id}
                onClick={() => {
                  setViewType(
                    view.id as
                    | "my_conversations"
                    | "shared_with_me"
                    | "all_company",
                  );
                  setCurrentPage(1);

                }}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${viewType === view.id
                  ? "bg-white text-indigo-600 shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
              >
                <span className="mr-2">{view.icon}</span>
                {view.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Conversations List */}
      {loading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-lg p-6 animate-pulse"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
                <div className="w-16 h-4 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Results Counter */}
          {conversations.length > 0 && (
            <div className="mb-4 text-sm text-gray-600">
              {getActiveFilterCount() > 0 || searchQuery.trim() ? (
                <>
                  Showing {conversations.length} result
                  {conversations.length !== 1 ? "s" : ""}
                  {searchQuery.trim() && ` for "${searchQuery}"`}
                  {getActiveFilterCount() > 0 &&
                    ` with ${getActiveFilterCount()} filter${getActiveFilterCount() !== 1 ? "s" : ""
                    } applied`}
                </>
              ) : (
                `${conversations.length} ${selectedStatus} conversation${conversations.length !== 1 ? "s" : ""
                }`
              )}
            </div>
          )}

          {conversations.length > 0 ? (
            <div className="space-y-3">
              {conversations.map((conversation) => (
                <div
                  key={conversation.id}
                  onClick={() => handleConversationClick(conversation.id)}
                  className={`bg-white border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${conversation.company_unread_count > 0
                    ? "border-indigo-200 bg-indigo-50/30"
                    : "border-gray-200 hover:border-gray-300"
                    }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      {/* Avatar */}
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${conversation.job_seeker_has_account
                          ? "bg-green-100 text-green-600"
                          : "bg-orange-100 text-orange-600"
                          }`}
                      >
                        <span className="text-sm font-medium">
                          {conversation.job_seeker_email
                            .charAt(0)
                            .toUpperCase()}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3
                            className={`font-medium text-sm truncate ${conversation.company_unread_count > 0
                              ? "text-gray-900"
                              : "text-gray-700"
                              }`}
                          >
                            {conversation.subject}
                          </h3>
                          <span
                            className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getPriorityColor(
                              conversation.priority,
                            )}`}
                          >
                            {conversation.priority}
                          </span>
                          {conversation.company_unread_count > 0 && (
                            <span className="bg-indigo-600 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                              {conversation.company_unread_count}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center space-x-3 text-xs text-gray-600 mb-2">
                          <span className="flex items-center">
                            <span className="mr-1">📧</span>
                            {conversation.job_seeker_email}
                          </span>
                          <span className="flex items-center">
                            <span className="mr-1">💬</span>
                            {conversation.message_count} msg
                            {conversation.message_count > 1 ? "s" : ""}
                          </span>
                          {conversation.job_title && (
                            <span className="flex items-center">
                              <span className="mr-1">💼</span>
                              {conversation.job_title}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Status Indicators */}
                    <div className="flex flex-col items-end space-y-1 ml-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">
                          {formatMessageTime(conversation.last_message_at)}
                        </span>
                        {/* Three-dot menu button */}
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(
                                activeDropdown === conversation.id
                                  ? null
                                  : conversation.id,
                              );
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                            </svg>
                          </button>

                          {activeDropdown === conversation.id && (
                            <DropdownMenu
                              conversation={conversation}
                              onStatusChange={handleStatusChange}
                              onPriorityChange={handlePriorityChange}
                              onShareConversation={handleShareConversation}
                              onTransferConversation={
                                handleTransferConversation
                              }
                              onClose={() => setActiveDropdown(null)}
                              onViewConversation={handleViewConversation}
                            />
                          )}
                        </div>
                      </div>
                      <div className="flex items-center">
                        {conversation.job_seeker_has_account ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            Invited
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">
                {getActiveFilterCount() > 0 || searchQuery.trim() ? "🔍" : "💬"}
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {getActiveFilterCount() > 0 || searchQuery.trim()
                  ? "No conversations match your search"
                  : `No ${selectedStatus} conversations`}
              </h3>
              <p className="text-gray-500 mb-6">
                {getActiveFilterCount() > 0 || searchQuery.trim()
                  ? "Try adjusting your search terms or filters to find what you're looking for."
                  : selectedStatus === "active"
                    ? "Start reaching out to candidates to begin conversations."
                    : `No ${selectedStatus} conversations found.`}
              </p>
              {getActiveFilterCount() > 0 || searchQuery.trim() ? (
                <button
                  onClick={clearSearchAndFilters}
                  className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Clear Search & Filters
                </button>
              ) : (
                selectedStatus === "active" && (
                  <button
                    onClick={() => navigate("/company/candidates")}
                    className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Browse Candidates
                  </button>
                )
              )}
            </div>
          )}
        </>
      )}

      <TablePagination
        currentPage={currentPage}
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onPageChange={(page) => setCurrentPage(page)}
        onRowsPerPageChange={(rows) => {
          setRowsPerPage(rows);
          setCurrentPage(1);
        }}
      />

      {/* Toast Notification */}
      {toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={closeToast}
        />
      )}

      {/* Share Conversation Modal */}
      {showShareModal && selectedConversationId && (
        <ShareConversationModal
          conversationId={selectedConversationId}
          onClose={() => {
            setShowShareModal(false);
            setSelectedConversationId(null);
          }}
          onSuccess={(message) => {
            showToast(message, "success");
            loadConversations(); // Refresh the list
          }}
          onError={(message) => {
            showToast(message, "error");
          }}
        />
      )}

      {/* Transfer Ownership Modal */}
      {showTransferModal && selectedConversationId && (
        <TransferOwnershipModal
          conversationId={selectedConversationId}
          onClose={() => {
            setShowTransferModal(false);
            setSelectedConversationId(null);
          }}
          onSuccess={(message) => {
            showToast(message, "success");
            loadConversations(); // Refresh the list
          }}
          onError={(message) => {
            showToast(message, "error");
          }}
        />
      )}
    </div>
  );
};

export default CompanyMessages;
